import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  CANDIDATES_LIST_GROUP_BY_LOCALSTORAGE_KEY,
  OrderDirection,
} from '@constants';
import { LocalStorageService } from '@services';
import { canonicalSeniority, candidateFacetKey, CandidateRegion } from '@utils';
import { CandidateColumnId, isCandidateColumnId } from '@components';

export type CandidateGroupingOptionType =
  | 'workstream-position'
  | 'position'
  | 'workstream'
  | 'seniority'
  | 'status'
  | 'none';

export interface ICandidatesViewState {
  query: string;
  positionIds: string[];
  regions: CandidateRegion[];
  seniorities: string[];
  statuses: string[];
  groupBy: CandidateGroupingOptionType;
  showRejected: boolean;
  columns: CandidateColumnId[];
  sort: { key: string; direction: OrderDirection };
}

const DefaultCandidateSort = {
  key: 'status',
  direction: OrderDirection.ASC,
};

export const EmptyCandidateFilters: Pick<
  ICandidatesViewState,
  'positionIds' | 'regions' | 'seniorities' | 'statuses'
> = {
  positionIds: [],
  regions: [],
  seniorities: [],
  statuses: [],
};

export const isDefaultSort = (sort: ICandidatesViewState['sort']): boolean =>
  sort.key === DefaultCandidateSort.key &&
  sort.direction === DefaultCandidateSort.direction;

const GroupingOptions: CandidateGroupingOptionType[] = [
  'workstream-position',
  'position',
  'workstream',
  'seniority',
  'status',
  'none',
];

const isGroupingOption = (
  value: string | null,
): value is CandidateGroupingOptionType =>
  value !== null &&
  GroupingOptions.includes(value as CandidateGroupingOptionType);

const readGroupBy = (): CandidateGroupingOptionType => {
  const stored = LocalStorageService.getState<string>(
    CANDIDATES_LIST_GROUP_BY_LOCALSTORAGE_KEY,
  );

  return isGroupingOption(stored) ? stored : 'position';
};

const splitParam = (value: string | null): string[] =>
  value ? value.split(',').filter(Boolean) : [];

const isDefaultColumns = (
  columns: CandidateColumnId[],
  defaultColumns: CandidateColumnId[],
): boolean =>
  columns.length === defaultColumns.length &&
  columns.every((id, index) => id === defaultColumns[index]);

export const useCandidatesViewState = (
  defaultColumns: CandidateColumnId[],
  knownPositionIds: string[],
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: entryState } = useLocation();

  const state: ICandidatesViewState = useMemo(() => {
    const rawSort = searchParams.get('sort');
    const [sortKey, sortDir] = rawSort ? rawSort.split(':') : [];
    const rawColumns = searchParams.get('cols');

    return {
      query: searchParams.get('q') ?? '',
      positionIds: splitParam(searchParams.get('positions')).filter((id) =>
        knownPositionIds.includes(id),
      ),
      regions: splitParam(searchParams.get('region')).filter(
        (value): value is CandidateRegion =>
          Object.values(CandidateRegion).includes(value as CandidateRegion),
      ),
      seniorities: splitParam(searchParams.get('seniority')).map(
        canonicalSeniority,
      ),
      statuses: splitParam(searchParams.get('status')).map(candidateFacetKey),
      groupBy: isGroupingOption(searchParams.get('group'))
        ? (searchParams.get('group') as CandidateGroupingOptionType)
        : readGroupBy(),
      showRejected: searchParams.get('rejected') === 'true',
      columns: rawColumns
        ? rawColumns.split(',').filter(isCandidateColumnId)
        : defaultColumns,
      sort:
        sortKey &&
        (sortDir === OrderDirection.ASC || sortDir === OrderDirection.DESC)
          ? { key: sortKey, direction: sortDir }
          : DefaultCandidateSort,
    };
  }, [searchParams, defaultColumns, knownPositionIds]);

  const write = useCallback(
    (patch: Partial<ICandidatesViewState>) => {
      const next = { ...state, ...patch };

      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);

          const set = (key: string, value: string | null) =>
            value ? params.set(key, value) : params.delete(key);

          set('q', next.query);
          set('positions', next.positionIds.join(','));
          set('region', next.regions.join(','));
          set('seniority', next.seniorities.join(','));
          set('status', next.statuses.join(','));
          set('group', next.groupBy === readGroupBy() ? null : next.groupBy);
          set('rejected', next.showRejected ? 'true' : null);
          set(
            'cols',
            isDefaultColumns(next.columns, defaultColumns)
              ? null
              : next.columns.join(','),
          );
          set(
            'sort',
            isDefaultSort(next.sort)
              ? null
              : `${next.sort.key}:${next.sort.direction}`,
          );

          return params;
        },
        { replace: true, state: entryState },
      );

      if (patch.groupBy !== undefined) {
        LocalStorageService.setState(
          CANDIDATES_LIST_GROUP_BY_LOCALSTORAGE_KEY,
          patch.groupBy,
        );
      }
    },
    [state, defaultColumns, setSearchParams, entryState],
  );

  const toggleSort = useCallback(
    (key: string) => {
      if (state.sort.key !== key) {
        write({ sort: { key, direction: OrderDirection.ASC } });
        return;
      }

      write({
        sort:
          state.sort.direction === OrderDirection.ASC
            ? { key, direction: OrderDirection.DESC }
            : DefaultCandidateSort,
      });
    },
    [state.sort, write],
  );

  const reset = useCallback(
    () =>
      write({
        query: '',
        ...EmptyCandidateFilters,
        showRejected: false,
        columns: defaultColumns,
        sort: DefaultCandidateSort,
      }),
    [write, defaultColumns],
  );

  const activeFilterCount =
    state.positionIds.length +
    state.regions.length +
    state.seniorities.length +
    state.statuses.length;

  const isDefault =
    !state.query &&
    activeFilterCount === 0 &&
    !state.showRejected &&
    isDefaultSort(state.sort) &&
    isDefaultColumns(state.columns, defaultColumns);

  return { state, write, toggleSort, reset, activeFilterCount, isDefault };
};
